import React, { useState } from 'react';

import dayjs from '~/timeInit';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { computeSurchargeMargin } from './computeSurchargeMargin';
import { WEEKDAYS, WEEKDAY_LABELS, daysSurchargedIn } from './surchargeGridsModel';
import { validateSurchargePeriod } from './surchargePeriodValidation';
import type { SurchargeGrids, SurchargeMargin, SurchargePeriod, SurchargePeriodFormValues, Weekday } from './types';

const BLANK = '-';

interface FormState {
  name: string;
  startDate: string;
  endDate: string;
  sellAud: string;
  costLocal: string;
  weekdays: Array<Weekday>;
}

const EMPTY_FORM: FormState = { name: '', startDate: '', endDate: '', sellAud: '', costLocal: '', weekdays: [] };

function toValues(form: FormState): SurchargePeriodFormValues {
  return {
    name: form.name,
    startDate: form.startDate,
    endDate: form.endDate,
    sellAud: form.sellAud === '' ? Number.NaN : Number(form.sellAud),
    costLocal: form.costLocal === '' ? null : Number(form.costLocal),
    weekdays: form.weekdays,
  };
}

function toForm(period: SurchargePeriod): FormState {
  return {
    name: period.name,
    startDate: period.startDate,
    endDate: period.endDate,
    sellAud: String(period.sellAud),
    costLocal: period.costLocal == null ? '' : String(period.costLocal),
    weekdays: [...period.weekdays],
  };
}

function describeDays(period: SurchargePeriod) {
  if (period.weekdays.length === 0) {
    return 'All days';
  }
  return WEEKDAYS.filter((weekday) => period.weekdays.includes(weekday))
    .map((weekday) => WEEKDAY_LABELS[weekday])
    .join(', ');
}

function describeAmounts(period: SurchargePeriod, margin: SurchargeMargin, days: number | null) {
  const costAud = margin.costAud == null ? BLANK : margin.costAud.toFixed(2);
  const marginPct = margin.marginFraction == null ? BLANK : `${(margin.marginFraction * 100).toFixed(1)}%`;
  return `Sell AUD ${period.sellAud} · Cost AUD ${costAud} · Margin ${marginPct} · Days surcharged ${days ?? BLANK}`;
}

interface Props {
  currencyCode: string;
  fxRateAudToNative: number | null;
  grids: SurchargeGrids;
  onAdd: (values: SurchargePeriodFormValues) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, values: SurchargePeriodFormValues) => void;
}

export default function SurchargePeriodsSection({
  currencyCode,
  fxRateAudToNative,
  grids,
  onAdd,
  onRemove,
  onUpdate,
}: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editedPeriodId, setEditedPeriodId] = useState<string | null>(null);
  // Copying or loading a session replaces every period, so an edit in progress becomes an add.
  const editingId = grids.periods.some((period) => period.id === editedPeriodId) ? editedPeriodId : null;
  const [errors, setErrors] = useState<Array<string>>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleSubmit = () => {
    const values = toValues(form);
    const validationErrors = validateSurchargePeriod(values);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (editingId) {
      onUpdate(editingId, values);
    } else {
      onAdd(values);
    }

    setForm(EMPTY_FORM);
    setEditedPeriodId(null);
    setErrors([]);
  };

  const handleEdit = (id: string) => {
    const period = grids.periods.find((item) => item.id === id);
    if (!period) {
      return;
    }

    setForm(toForm(period));
    setEditedPeriodId(id);
    setErrors([]);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditedPeriodId(null);
    setErrors([]);
  };

  const confirmDelete = () => {
    if (deleteId) {
      onRemove(deleteId);
      if (editingId === deleteId) {
        handleCancel();
      }
    }
    setDeleteId(null);
  };

  const toggleWeekday = (weekday: Weekday) => {
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(weekday)
        ? prev.weekdays.filter((day) => day !== weekday)
        : [...prev.weekdays, weekday],
    }));
  };

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography fontWeight={600} variant="subtitle1">
              {editingId ? 'Edit surcharge period' : 'New surcharge period'}
            </Typography>

            <TextField
              fullWidth
              label="Period name"
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="e.g. Chinese New Year"
              size="small"
              value={form.name}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateWidget
                fullWidth
                label="From"
                onChange={(value) => setForm((prev) => ({ ...prev, startDate: value }))}
                slotProps={{ textField: { size: 'small' } }}
                value={form.startDate}
              />
              <DateWidget
                fullWidth
                label="To"
                // Opens on the From month and refuses an earlier date, so a reversed range cannot
                // be typed in the first place; the validator stays as the backstop.
                minDate={form.startDate ? dayjs(form.startDate) : undefined}
                onChange={(value) => setForm((prev) => ({ ...prev, endDate: value }))}
                slotProps={{ textField: { size: 'small' } }}
                value={form.endDate}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                label="Sell AUD"
                onChange={(event) => setForm((prev) => ({ ...prev, sellAud: event.target.value }))}
                size="small"
                type="number"
                value={form.sellAud}
              />
              <TextField
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                label={`Cost ${currencyCode}`}
                onChange={(event) => setForm((prev) => ({ ...prev, costLocal: event.target.value }))}
                size="small"
                type="number"
                value={form.costLocal}
              />
            </Stack>

            <Box>
              <Typography color="text.secondary" variant="body2">
                Days of week. Leave every box unticked to surcharge every date in the range.
              </Typography>
              <FormGroup row>
                {WEEKDAYS.map((weekday) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.weekdays.includes(weekday)}
                        onChange={() => toggleWeekday(weekday)}
                        size="small"
                      />
                    }
                    key={weekday}
                    label={WEEKDAY_LABELS[weekday]}
                  />
                ))}
              </FormGroup>
            </Box>

            {errors.length > 0 && (
              <Alert severity="error" variant="outlined">
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button onClick={handleSubmit} size="small" variant="contained">
                {editingId ? 'Update' : 'Add'}
              </Button>
              {editingId && (
                <Button onClick={handleCancel} size="small" variant="text">
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {grids.periods.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No surcharge periods yet. The weekday grid above covers recurring amounts; add a period here for a dated
          surcharge such as a public holiday.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {grids.periods.map((period) => {
            const margin = computeSurchargeMargin(
              { sellAud: period.sellAud, costLocal: period.costLocal },
              fxRateAudToNative ?? 0,
            );
            const days = daysSurchargedIn(period);

            return (
              <Card key={period.id} variant="outlined">
                <CardContent>
                  <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography fontWeight={600} variant="subtitle2">
                        {period.name}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {period.startDate} to {period.endDate} · {describeDays(period)} · All rooms
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {describeAmounts(period, margin, days)}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <IconButton aria-label="Edit surcharge period" onClick={() => handleEdit(period.id)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete surcharge period"
                        onClick={() => setDeleteId(period.id)}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete surcharge period</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this surcharge period? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
